/**
 * Снимок сцены в PNG.
 *
 * Отдельная экспортная SVG, а не снимок DOM: библиотека для съёмки всего
 * дерева тянет чужие шрифты, чужие тени и панель управления вместе с
 * графиком, а нужен ровно график.
 *
 * Цвета приходят параметром. Внутри `<img>` браузер не видит ни каскада, ни
 * переменных темы: `var(--fn-0)` в сериализованной SVG превращается в чёрный,
 * и все четыре кривые становятся одной.
 */

import { curveFeatures, formulaText, pointText } from "./math";
import { sampleCurve, toPathData } from "./sampling";
import type { LabScene } from "./model";
import { gridStep, gridTicks, toScreen, worldBounds, type PlotSize } from "./viewport";

export type ExportPalette = {
  background: string;
  grid: string;
  axis: string;
  text: string;
  /** Цвет слота палитры — по индексу `SceneCurve.slot`. */
  curves: string[];
};

/** Подпись деления: столько знаков, сколько несёт сам шаг. */
function tickLabel(value: number, step: number): string {
  const decimals = Math.max(0, Math.min(6, -Math.floor(Math.log10(step))));
  const text = Math.abs(value) < step / 2 ? "0" : value.toFixed(decimals);
  return text.replace(/^-/, "−");
}

/** Текст внутри SVG — данные, а не разметка: формула может содержать «<». */
function escapeText(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function round(value: number): string {
  return value.toFixed(2);
}

/** Насколько близко к краю подпись деления уже не помещается целиком. */
const EDGE = 20;

/** Высота строки легенды и отступ снизу под неё. */
const LEGEND_LINE = 22;
const LEGEND_PAD = 14;

/**
 * Экспортная SVG сцены: плоскость, видимые кривые, характерные точки,
 * легенда с формулами и текущий диапазон координат. Панели, ползунков и
 * следа предыдущего состояния здесь нет — они не про график.
 */
export function sceneSvg(scene: LabScene, size: PlotSize, palette: ExportPalette): string {
  const visible = scene.curves.filter((curve) => curve.visible);
  const legendHeight = visible.length * LEGEND_LINE + LEGEND_PAD * 2;
  const height = size.height + legendHeight;
  const camera = scene.camera;
  const bounds = worldBounds(camera, size);
  const step = gridStep(camera.scale);
  const origin = toScreen(camera, size, { x: 0, y: 0 });
  const axisY = Math.max(16, Math.min(size.height - 18, origin.y));
  const axisX = Math.max(26, Math.min(size.width - 16, origin.x));
  const color = (slot: number) => palette.curves[slot % palette.curves.length] ?? palette.text;

  const parts: string[] = [];
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${round(size.width)}" height="${round(height)}" ` +
      `viewBox="0 0 ${round(size.width)} ${round(height)}" font-family="Golos Text, system-ui, sans-serif">`,
  );
  parts.push(`<rect width="100%" height="100%" fill="${palette.background}"/>`);

  parts.push(`<g stroke="${palette.grid}" stroke-width="1">`);
  for (const x of gridTicks(bounds.minX, bounds.maxX, step)) {
    const at = round(toScreen(camera, size, { x, y: 0 }).x);
    parts.push(`<line x1="${at}" y1="0" x2="${at}" y2="${round(size.height)}"/>`);
  }
  for (const y of gridTicks(bounds.minY, bounds.maxY, step)) {
    const at = round(toScreen(camera, size, { x: 0, y }).y);
    parts.push(`<line x1="0" y1="${at}" x2="${round(size.width)}" y2="${at}"/>`);
  }
  parts.push(`</g>`);

  parts.push(`<g stroke="${palette.axis}" stroke-width="1.5">`);
  parts.push(`<line x1="0" y1="${round(axisY)}" x2="${round(size.width)}" y2="${round(axisY)}"/>`);
  parts.push(`<line x1="${round(axisX)}" y1="0" x2="${round(axisX)}" y2="${round(size.height)}"/>`);
  parts.push(`</g>`);

  parts.push(`<g fill="${palette.text}" font-size="12">`);
  for (const x of gridTicks(bounds.minX, bounds.maxX, step)) {
    if (Math.abs(x) < step / 2) continue;
    const at = toScreen(camera, size, { x, y: 0 }).x;
    // Подпись у самого края обрезается посередине, и «−10» читается как «10».
    if (at < EDGE || at > size.width - EDGE) continue;
    parts.push(`<text x="${round(at)}" y="${round(axisY + 16)}" text-anchor="middle">${escapeText(tickLabel(x, step))}</text>`);
  }
  for (const y of gridTicks(bounds.minY, bounds.maxY, step)) {
    if (Math.abs(y) < step / 2) continue;
    const at = round(toScreen(camera, size, { x: 0, y }).y + 4);
    parts.push(`<text x="${round(axisX - 8)}" y="${at}" text-anchor="end">${escapeText(tickLabel(y, step))}</text>`);
  }
  parts.push(`</g>`);

  for (const entry of visible) {
    const stroke = color(entry.slot);
    const active = entry.id === scene.activeId;
    parts.push(`<g stroke="${stroke}" fill="none" stroke-width="${active ? 3 : 2}" stroke-linecap="round" stroke-linejoin="round">`);
    for (const path of sampleCurve(entry.curve, camera, size)) {
      const data = toPathData(path);
      if (data !== "") parts.push(`<path d="${data}"/>`);
    }
    parts.push(`</g>`);

    const features = curveFeatures(entry.curve);
    for (const point of features.points) {
      const at = toScreen(camera, size, { x: point.x.value, y: point.y.value });
      if (!Number.isFinite(at.x) || !Number.isFinite(at.y)) continue;
      if (at.x < 0 || at.x > size.width || at.y < 0 || at.y > size.height) continue;
      parts.push(
        `<circle cx="${round(at.x)}" cy="${round(at.y)}" r="4.5" fill="${palette.background}" stroke="${stroke}" stroke-width="2"/>`,
      );
      if (active) {
        parts.push(
          `<text x="${round(at.x + 9)}" y="${round(at.y - 9)}" fill="${palette.text}" font-size="12">${escapeText(pointText(point))}</text>`,
        );
      }
    }
  }

  let line = size.height + LEGEND_PAD + 12;
  for (const entry of visible) {
    const stroke = color(entry.slot);
    parts.push(
      `<line x1="16" y1="${round(line - 4)}" x2="46" y2="${round(line - 4)}" stroke="${stroke}" stroke-width="${entry.id === scene.activeId ? 3 : 2}" stroke-linecap="round"/>`,
    );
    parts.push(
      `<text x="56" y="${round(line)}" fill="${palette.text}" font-size="14">${escapeText(formulaText(entry.curve))}</text>`,
    );
    line += LEGEND_LINE;
  }

  const range =
    `x от ${tickLabel(bounds.minX, step)} до ${tickLabel(bounds.maxX, step)}, ` +
    `y от ${tickLabel(bounds.minY, step)} до ${tickLabel(bounds.maxY, step)}`;
  parts.push(
    `<text x="${round(size.width - 16)}" y="${round(height - LEGEND_PAD)}" text-anchor="end" fill="${palette.text}" font-size="12" opacity="0.7">${escapeText(range)}</text>`,
  );

  parts.push(`</svg>`);
  return parts.join("");
}

/** Читает цвета сцены из уже применённой темы. */
export function readPalette(element: Element, slots = 4): ExportPalette {
  const style = getComputedStyle(element);
  const token = (name: string, fallback: string) => style.getPropertyValue(name).trim() || fallback;
  return {
    background: token("--surface", "#ffffff"),
    grid: token("--fn-grid", "#d8d2c4"),
    axis: token("--fn-axis", "#7a7368"),
    text: token("--ink", "#1c1a17"),
    curves: Array.from({ length: slots }, (_, slot) => token(`--fn-${slot}`, "#2a5db0")),
  };
}

/**
 * Растеризация: SVG → canvas → PNG. Двойное разрешение, потому что снимок
 * чаще всего смотрят увеличенным, а вектор внутри `<img>` масштабируется
 * только при отрисовке.
 */
export async function scenePngBlob(svg: string, size: PlotSize, ratio = 2): Promise<Blob> {
  const source = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(source);
  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("Снимок не построился"));
      image.src = url;
    });
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(image.width * ratio) || Math.round(size.width * ratio);
    canvas.height = Math.round(image.height * ratio) || Math.round(size.height * ratio);
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Снимок не построился");
    context.scale(ratio, ratio);
    context.drawImage(image, 0, 0);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Снимок не построился"))), "image/png");
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function downloadScenePng(
  scene: LabScene,
  size: PlotSize,
  palette: ExportPalette,
  fileName = "график.png",
): Promise<void> {
  const blob = await scenePngBlob(sceneSvg(scene, size, palette), size);
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
  // Отзыв сразу после click() иногда опережает саму загрузку в Safari.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
